/** @file batchActions: optimistic dispatch with rollback, server undo/redo, commits heard on the live socket. */
import type { Command } from '@zilo/pose3d-commands/types/command';
import type { Reply } from '@zilo/pose3d-commands/types/reply';
import type { Pose3DError } from '@zilo/pose3d-std/errors/pose3DError';
import type { EditorActions } from '../state/editorTypes';
import { asPose3DError } from './asPose3DError';
import { errorText } from './errorText';
import type { LiveCommit } from './liveSync';
import { publishQueue } from './publishQueue';
import { readHistory } from './readHistory';
import type { ServerContext } from './serverContext';

/** The batch half of the server store, plus the live commit handler `puppetLoading` wires to the socket. */
export type BatchActions = Pick<EditorActions, 'dispatch' | 'undo' | 'redo'> & { onCommit: (commit: LiveCommit) => void };

const failedReplies = (commands: Command[], error: Pose3DError): Reply[] =>
  commands.map((c) => ({ ok: false, ...(c.id === undefined ? {} : { id: c.id }), error, warnings: [] }));

/** Shows one error: status bar `lastError` and one toast. */
function report(ctx: ServerContext, error: Pose3DError): void {
  const text = errorText(error);
  ctx.set((s) => ({ puppet: { ...s.puppet, lastError: text } }));
  ctx.get().notify('error', text);
}

/** A committed revision: refetch and rebuild after the debounce. */
function committed(ctx: ServerContext): void {
  ctx.dirty = true;
  void ctx.schedule.schedule();
}

/** The label the server takes: trimmed, at most 80 characters, none when empty. */
const labelOf = (label: string | undefined): { label?: string } => {
  const l = label?.trim().slice(0, 80) ?? '';
  return l === '' ? {} : { label: l };
};

async function send(ctx: ServerContext, key: number, commands: Command[], token: number, label?: string): Promise<Reply[]> {
  const q = ctx.queue;
  const id = ctx.get().puppet.id;
  if (token !== ctx.token || q === null || id === null) return failedReplies(commands, asPose3DError(new Error('puppet closed')));
  try {
    const reply = await ctx.api.puppets.commands(id, { commands, ifRevision: q.confirmed().revision, ...labelOf(label) });
    if (token !== ctx.token) return reply.replies;
    const failed = reply.replies.find((r) => !r.ok);
    if (failed?.ok === false) {
      q.drop(key);
      publishQueue(ctx);
      report(ctx, failed.error);
      return reply.replies;
    }
    q.settle(key, reply.revision);
    ctx.set((s) => ({ puppet: { ...s.puppet, lastError: null } }));
    publishQueue(ctx);
    committed(ctx);
    await readHistory(ctx, id, token);
    return reply.replies;
  } catch (e) {
    const error = asPose3DError(e);
    if (token !== ctx.token) return failedReplies(commands, error);
    q.drop(key);
    publishQueue(ctx);
    report(ctx, error);
    committed(ctx);
    return failedReplies(commands, error);
  }
}

const sameCommands = (a: Command[], b: Command[]): boolean => JSON.stringify(a) === JSON.stringify(b);

/**
 * Batches, undo and redo run one at a time through `ctx.chain`, so each batch's `ifRevision` is the latest confirmed
 * one and a held Ctrl+Z / Ctrl+Y steps once per answer; a step the known history says is empty is skipped.
 */
export function batchActions(ctx: ServerContext): BatchActions {
  const enqueue = <T>(job: () => Promise<T>): Promise<T> => {
    const run = ctx.chain.then(job);
    ctx.chain = run.catch(() => undefined);
    return run;
  };
  const dispatch = (commands: Command[], label?: string): Promise<Reply[]> => {
    const q = ctx.queue;
    if (q === null) return Promise.reject(new Error('dispatch: no puppet open'));
    const key = q.push(commands);
    publishQueue(ctx);
    const token = ctx.token;
    return enqueue(() => send(ctx, key, commands, token, label));
  };
  const step = (direction: 'undo' | 'redo') => (): Promise<void> => enqueue(async () => {
    const { id, history } = ctx.get().puppet;
    if (id === null || (history !== null && history[direction] === null)) return;
    const token = ctx.token;
    try {
      await ctx.api.puppets[direction](id);
      committed(ctx);
    } catch (e) {
      report(ctx, asPose3DError(e));
    }
    await readHistory(ctx, id, token);
  });
  const onCommit = (commit: LiveCommit): void => {
    const q = ctx.queue;
    if (q === null || commit.revision <= q.confirmed().revision) return;
    const head = q.head();
    if (head !== undefined && sameCommands(head.commands, commit.commands)) q.settle(head.key, commit.revision);
    else q.remote(commit.commands, commit.revision);
    publishQueue(ctx);
    committed(ctx);
    const id = ctx.get().puppet.id;
    if (id !== null) void readHistory(ctx, id, ctx.token);
  };
  return { dispatch, undo: step('undo'), redo: step('redo'), onCommit };
}
